import React from "react";
import { Link as GatsbyLink, GatsbyLinkProps, withPrefix } from "gatsby";
import OutboundLink from "../OutboundLink/OutboundLink";
import { useAnalyticsContext } from "../../utils/analytics";
import { useGatsbyLocation } from "../../utils/useGatsbyLocation";
import { useLanguage } from "../../i18n/LanguageContext";
import { siteLanguageToMakeCodeLanguage } from "../../model/language";

const isInternal = (href: string) => /^\/(?!\/)/.test(href);

export interface LinkProps<T> extends GatsbyLinkProps<T> {
  analyticsDownloadId?: string;
}

const isSamePageFragmentLink = (
  currentUrlPathname: string,
  targetUrl?: string
): boolean => {
  if (!targetUrl) {
    return false;
  }
  if (targetUrl.startsWith("#")) {
    return true;
  }
  // Relies on our consistent approach to on-site link generation.
  return targetUrl.startsWith(`${currentUrlPathname}#`);
};

// tslint:disable-next-line: function-name
function Link<T>({
  // Gatsby props not supported by regular links.
  to,
  activeClassName,
  activeStyle,
  partiallyActive,
  replace,
  state,
  children,
  getProps,
  // We're discarding this for now due to typing issues.
  ref,
  // Custom props
  analyticsDownloadId,
  // Pass through
  ...rest
}: LinkProps<T>) {
  const language = useLanguage();
  const { pathname: currentUrlPathname } = useGatsbyLocation();
  const { siteArea } = useAnalyticsContext();
  if (isSamePageFragmentLink(currentUrlPathname, to)) {
    return (
      <a {...rest} href={to}>
        {children}
      </a>
    );
  }
  if (
    typeof to === "undefined" ||
    (typeof to === "string" && !isInternal(to)) ||
    to.startsWith("/downloads/")
  ) {
    const gtagProps = analyticsDownloadId
      ? {
          gtagAction: "download",
          gtagCategory: siteArea,
          gtagLabel: analyticsDownloadId
        }
      : {};
    const externalLink = enhanceExternalLink(language, to);
    return (
      <OutboundLink
        href={externalLink ? withPrefix(externalLink) : undefined}
        {...gtagProps}
        {...rest}
      >
        {children}
      </OutboundLink>
    );
  }
  return (
    <GatsbyLink
      to={to}
      activeClassName={activeClassName}
      activeStyle={activeStyle}
      partiallyActive={partiallyActive}
      replace={replace}
      state={state}
      getProps={getProps}
      {...rest}
    >
      {children}
    </GatsbyLink>
  );
}

const enhanceExternalLink = (
  language: string,
  to: string | undefined
): string | undefined => {
  // Links to MakeCode and the Python editor can be annotated with language info.
  // We do it here so it's comprehensive and deals with ad-hoc links in rich text.
  const code = language === "en" ? undefined : language;
  if (!to) {
    return to;
  }
  if (new RegExp("^https://makecode.microbit.org").test(to)) {
    return withFragmentParam(to, "lang", siteLanguageToMakeCodeLanguage(code));
  }
  if (
    new RegExp(
      "^https://(python|ml|data|classroom|createai).microbit.org"
    ).test(to)
  ) {
    return withQueryParam(to, "l", code);
  }
  return to;
};

// Replace with URL when we can drop IE.
// We use fragments for MakeCode as otherwise you can't change
// language as the query string stays in the URL and wins.

const withQueryParam = (
  url: string,
  paramName: string,
  paramValue: string | undefined
) => {
  if (!paramValue) {
    return url;
  }
  const part = `${paramName}=${encodeURIComponent(paramValue)}`;
  const queryIndex = url.indexOf("?");
  if (queryIndex !== -1) {
    return insertBefore(url, queryIndex + 1, `${part}&`);
  }
  const fragmentIndex = url.indexOf("#");
  if (fragmentIndex !== -1) {
    return insertBefore(url, fragmentIndex, `?${part}`);
  }
  return `${url}?${part}`;
};

const withFragmentParam = (
  url: string,
  paramName: string,
  paramValue: string | undefined
) => {
  if (!paramValue) {
    return url;
  }
  const part = `${paramName}=${encodeURIComponent(paramValue)}`;
  const fragmentIndex = url.indexOf("#");
  if (fragmentIndex !== -1) {
    return `${url}&${part}`;
  }
  return `${url}#${part}`;
};

const insertBefore = (text: string, index: number, insertion: string) => {
  return [text.substring(0, index), insertion, text.substring(index)].join("");
};

export default Link;
